/**
 * Helpers for uploaded logo files: a preview URL and image preparation for
 * text recognition.
 *
 * Nothing here relies on blob: URLs being allowed to load as images. A
 * production server's Content-Security-Policy often permits only 'self' and
 * data: for <img>, and the file's own MIME type can't be trusted either —
 * Windows hands `.jfif` files over as "image/jfif", or with no type at all.
 * So files are decoded from their bytes (createImageBitmap, which sniffs the
 * real format) and previews are data: URLs.
 */

const MAX_SIDE = 1600;

const EXT_TYPES = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  jfif: "image/jpeg",
  jpe: "image/jpeg",
  pjpeg: "image/jpeg",
  pjp: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  svg: "image/svg+xml",
};

/** A MIME type the browser will accept, whatever the OS reported. */
function normalizedType(file) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const reported = (file.type || "").toLowerCase();
  if (EXT_TYPES[ext]) return EXT_TYPES[ext];
  if (reported.startsWith("image/") && reported !== "image/jfif")
    return reported;
  return "image/jpeg";
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("This file couldn't be read."));
    reader.readAsDataURL(new Blob([file], { type: normalizedType(file) }));
  });
}

/** data: URL for showing the uploaded logo in an <img>. */
export function logoPreviewUrl(file) {
  return readAsDataUrl(file);
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error("This file couldn't be opened as an image."));
    img.src = src;
  });
}

/** Decode to something drawable plus its size. Bitmap first, <img> as fallback. */
async function decode(file) {
  const typed = new Blob([file], { type: normalizedType(file) });

  // Not for SVG — createImageBitmap can't rasterise it.
  if (
    typed.type !== "image/svg+xml" &&
    typeof createImageBitmap === "function"
  ) {
    try {
      const bitmap = await createImageBitmap(typed);
      return { source: bitmap, width: bitmap.width, height: bitmap.height };
    } catch {
      // Fall through to the <img> route.
    }
  }

  const img = await loadImageElement(await readAsDataUrl(file));
  return {
    source: img,
    // SVGs without a width/height report 0 — fall back to a sensible size.
    width: img.naturalWidth || 1024,
    height: img.naturalHeight || 1024,
  };
}

/**
 * Redraw the upload onto a canvas and re-encode as JPEG. That accepts any
 * format the browser can show, flattens transparency onto white (a
 * transparent PNG with black text would otherwise be read as black on
 * black), and caps the size so a 12 MP photo isn't a multi-megabyte request.
 *
 * @param {File} file
 * @returns {Promise<{ image: string, mimeType: string }>} base64 without a data: prefix
 */
export async function prepareLogoForOcr(file) {
  const { source, width, height } = await decode(file);

  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  // Very small logos read far better when enlarged a little.
  const upscale = Math.max(width, height) < 600 ? 2 : 1;
  const factor = scale * upscale;

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * factor));
  canvas.height = Math.max(1, Math.round(height * factor));

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  if (typeof source.close === "function") source.close();

  const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
  return { image: dataUrl.split(",")[1], mimeType: "image/jpeg" };
}
